import { useMemo } from "react";
import { Area, AreaChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import { formatValueCurrency } from "@/components/widgets/helpers/FormattingHelpers";
import { componentClassNames } from "@/components/widgets/Quote/config";
import type { FinancialWithPeriod } from "../../../types";

const chartConfig = {
	freeCashFlow: {
		label: "FCF",
		color: "var(--light-gray)",
	},
} satisfies ChartConfig;

export const FreeCashFlowChart = ({
	data,
}: {
	data: FinancialWithPeriod[];
}) => {
	const chartData = useMemo(() => {
		return data
			.filter((item) => item?.calculations?.freecashflow != null)
			.map((item) => ({
				freeCashFlow: item?.calculations?.freecashflow,
				period: item?.period,
			}));
	}, [data]);

	if (!chartData.length) return null;

	return (
		<Card className={componentClassNames} data-testid="free-cash-flow-chart">
			<CardHeader className="p-0">
				<CardTitle className="text-center">Free Cash Flow</CardTitle>
			</CardHeader>
			<CardContent className="p-0">
				<ChartContainer config={chartConfig} className="w-full">
					<AreaChart accessibilityLayer data={chartData}>
						<XAxis
							dataKey="period"
							tickLine={false}
							axisLine={false}
							tickMargin={0}
						/>
						<YAxis
							tickLine={false}
							axisLine={false}
							tickMargin={0}
							tickFormatter={(value) => formatValueCurrency(value)}
							width={50}
						/>
						<ChartTooltip
							cursor={false}
							content={(props) => (
								<ChartTooltipContent
									{...props}
									indicator="line"
									valueFormatter={formatValueCurrency}
									hideIndicator
								/>
							)}
						/>

						<defs>
							<linearGradient id="fillFreeCashFlow" x1="0" y1="0" x2="0" y2="1">
								<stop
									offset="5%"
									stopColor="var(--color-freeCashFlow)"
									stopOpacity={0.8}
								/>
								<stop
									offset="95%"
									stopColor="var(--color-freeCashFlow)"
									stopOpacity={0.1}
								/>
							</linearGradient>
						</defs>

						<Area
							dataKey="freeCashFlow"
							type="natural"
							fill="url(#fillFreeCashFlow)"
							fillOpacity={0.4}
							stroke="var(--color-freeCashFlow)"
						/>
						<ChartLegend content={<ChartLegendContent />} />
					</AreaChart>
				</ChartContainer>
			</CardContent>
		</Card>
	);
};
